import { Project } from '../../models/project/project.model';
import Utils from '../../util';
import { LooseObject } from '../../models/loose-object.model';
import { ScenarioInput } from '../../models/results/scenario-comparison/scenario-input.model';
import { ProjectInputItem } from '../../models/data-structures/project/project-input-item.model';
import { AffectedScenarioInput } from '../../models/results/scenario-comparison/affected-scenario-input.model';
import { Scenario } from '../../models/results/scenario-comparison/scenario.model';

export class ProjectInputValidator {

  constructor() {
  }

  /**
   * Returns a map containing any invalid scenarios with a list of invalid inputs. This is used to display
   * invalid scenarios information to the user.
   * @param currentProject is the current Project used to determine what has changed since the scenarios
   * were created.
   * @param projectState is an optional Project state that can be provided to check the scenarios against.
   */
  static getInvalidScenarioInputs(currentProject: Project, projectState?: Project): Map<string, string[]> {
    const invalidScenarios = new Map<string, string[]>();

    // check for scenarios
    if (currentProject?.scenarioComparison?.scenarios) {
      const scenarios = currentProject.scenarioComparison.scenarios;

      for (const scenario of scenarios) {
        const invalidInputs: string[] = [];

        // check each scenario for inputs
        if (scenario.inputs) {
          const inputs = scenario.inputs;

          for (const input of inputs) {
            // if input is part of bmp curve coefficient skip it
            if (input.path.includes('curve coefficient')) continue;

            // if project state is provided we will check that instead of the current project
            if (projectState) {
              if (!this.pathIsValid(input, projectState)) {
                invalidInputs.push(input.id);
              }
            } else {
              if (!this.pathIsValid(input, currentProject)) {
                invalidInputs.push(input.id);
              }
            }
          }
        }
        // if invalid inputs were found add them to the return map;
        if (invalidInputs?.length > 0) {
          invalidScenarios.set(scenario.name, invalidInputs);
        }
      }
    }

    return invalidScenarios;
  }

  /**
   * Determines if the path of the provided scenario input is valid using the provided project.
   * @param input is the scenario inputs being checked.
   * @param project is the project the scenario input will be checked with.
   */
  static pathIsValid(input: ScenarioInput, project: Project): boolean {
    if (!input.path || input.path.length < 1) return false;
    let cur: any = Utils.deepCopy(project);
    for (const id of input.path) {
      if (cur === null || cur === undefined) return false;
      if (Array.isArray(cur)) {
        const curArray: any[] = cur;
        // if we have a uuid, use it. If not, look for the name, if that doesn't work, treat is as an index
        if (this.doesLookLikeUUID(id)) {
          cur = curArray.find(item => item['uuid'] === id);
        } else {
          const byName = cur.find(item => input.id.includes(item['name']));
          if (byName) {
            cur = byName;
          } else if (!Utils.isNumeric(id) || curArray.length <= Number(id)) {
            // if the index doesn't work, it's invalid
            console.error('error path: ' + input.path + ' in index ' + id);
            return false;
          } else {
            cur = curArray[Number(id)];
          }
        }
      } else {
        const curObj: LooseObject = cur;
        if (curObj[id] === null || curObj[id] === undefined) {
          console.error('error path:' + input.id + ' , ' + id);
          return false;
        }
        cur = curObj[id];
      }
    }
    return true;
  }

  /**
   * Determines if the is string contains a uuid based on regex.
   * @param id is a scenario input id string.
   */
  static doesLookLikeUUID(id: string): boolean {
    const uuidRegex = /\b[0-9a-f]{8}\b-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-\b[0-9a-f]{12}\b/;
    return uuidRegex.test(id);

  }

  /**
   * Returns a map of scenarios and inputs that no longer have the same baseline value. This is used
   * to show the user which of their scenarios and inputs are affected by recent changes made to
   * the project and simulation.
   * @param scenarios
   * @param projectInputs is a list of project inputs used to determine which changes are present
   * in the scenarios.
   */
  static getAffectedInputs(
    scenarios: Scenario[],
    projectInputs: ProjectInputItem[]): Map<string, AffectedScenarioInput[]> {
    if (!scenarios || scenarios.length < 1) return null;

    const affectedScenarioMap = new Map<string, AffectedScenarioInput[]>();

    for (const scenario of scenarios) {
      const affectedInputs: AffectedScenarioInput[] = [];
      const inputs = scenario.inputs;

      for (const input of inputs) {

        if (input.path.includes('biogasShareData')) continue;

        const affectedProjectInput = projectInputs.find(i => i.id === input.id && i.value !== input.baselineValue);

        if (affectedProjectInput) {
          affectedInputs.push(new AffectedScenarioInput(input.id, input.baselineValue, affectedProjectInput.value));
        }
      }
      if (affectedInputs?.length > 0) {
        affectedScenarioMap.set(scenario.name, affectedInputs);
      }
    }
    return affectedScenarioMap;
  }
}

